import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { Picture } from "@/components/wpl/image/src/index.ts";
import { wacToken } from "@/libraries/design/foundations/index.ts";

export interface CollectionPricingHeroProps {
    collectionImage: {
        src: string;
        alt: string;
        height?: number;
        width?: number;
    };
    products: CollectionPricingHeroProduct[];
    orientation?: "vertical" | "horizontal";
}

export interface CollectionPricingHeroProduct {
    name?: string;
    image: {
        src: string;
        alt: string;
    };
}

const horizontalOrientationCss = css({
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gridTemplateRows: "repeat(2, auto)",
    gap: token("space.400"),
    columnGap: token("space.400"),
    rowGap: token("space.200"),
    fontFamily: wacToken("font.charlie.display"),
    fontWeight: 400,
    fontSize: "1.15rem"
});

const verticalOrientationCss = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.200")
});

const logoCss = css({
    display: "flex",
    alignItems: "center",
    gap: token("space.075"),
    width: "141px"
});

const verticalLogoCss = css({
    display: "flex",
    alignItems: "center",
    height: "20px"
});

export const CollectionPricingHero = (props: CollectionPricingHeroProps) => {
    const orientation = props.orientation || "horizontal";
    const intl = useIntl();

    const appMessages = useAppMessages();

    return (
        <div
            css={css({
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginBlockTop: "20px"
            })}
        >
            <div
                css={css({
                    width: "fit-content",
                    maxWidth: "684px"
                })}
            >
                <div
                    css={css({
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: token("space.200"),
                        padding: `${token("space.250")} ${token("space.200")}`,
                        [media.above.sm]: {
                            flexDirection: "row",
                            gap: token("space.400"),
                            padding: `${token("space.250")} 44px`
                        }
                    })}
                >
                    <div
                        css={css({
                            display: "flex",
                            alignItems: "center"
                        })}
                    >
                        <Picture
                            image={{
                                src: props.collectionImage.src,
                                alt: props.collectionImage.alt,
                                height: props.collectionImage.height,
                                width: props.collectionImage.width
                            }}
                        />
                    </div>

                    <div
                        css={css({
                            display: "flex",
                            flexDirection: "column",
                            gap: 12
                        })}
                    >
                        <span
                            css={css({
                                fontFamily: wacToken("font.charlie.text"),
                                fontSize: 10,
                                fontWeight: 400,
                                textTransform: "uppercase",
                                color: wacToken("color.neutral.light800"),
                                lineHeight: "16px",
                                textAlign: "center",
                                [media.above.sm]: {
                                    textAlign: "initial"
                                }
                            })}
                        >
                            {intl.formatMessage({
                                id: "CollectionPricingHero.includes",
                                defaultMessage: "Includes"
                            })}
                        </span>
                        <div
                            css={[
                                orientation === "horizontal"
                                    ? horizontalOrientationCss
                                    : verticalOrientationCss
                            ]}
                        >
                            {props.products.map((product, key) => {
                                return (
                                    <div
                                        css={[
                                            orientation === "horizontal"
                                                ? logoCss
                                                : verticalLogoCss
                                        ]}
                                        key={key}
                                    >
                                        <Picture
                                            image={{
                                                src: product.image.src,
                                                alt: product.image.alt
                                            }}
                                            style={{ height: "20px" }}
                                            css={css({
                                                paddingInlineEnd:
                                                    orientation === "vertical"
                                                        ? token("space.075")
                                                        : 0
                                            })}
                                        />
                                        {product.name}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div
                    css={css({
                        width: "100%",
                        borderBlockStart: `solid 1px ${wacToken(
                            "color.neutral.dark1100"
                        )}`
                    })}
                />

                <div>
                    <span
                        css={css({
                            color: wacToken("color.primary.black"),
                            display: "block",
                            textAlign: "center",
                            fontFamily: wacToken("font.charlie.text"),
                            fontSize: "16px",
                            fontStyle: "normal",
                            fontWeight: 700,
                            lineHeight: "26px",
                            padding: `${token("space.200")} 0`
                        })}
                    >
                        {intl.formatMessage({
                            id: "CollectionPricingHero.platformAppsHeading",
                            defaultMessage: "Plus all Atlassian Platform Apps"
                        })}
                    </span>

                    <div
                        css={css({
                            display: "flex",
                            justifyContent: "center"
                        })}
                    >
                        <div
                            css={css({
                                display: "flex",
                                flexWrap: "wrap",
                                justifyContent: "center",
                                gap: token("space.100"),
                                [media.above.sm]: {
                                    gap: token("space.200")
                                }
                            })}
                        >
                            <Subproduct
                                src="https://dam-cdn.atl.orangelogic.com/AssetLink/k383m26br4w8fu6i68s0qbnym881f0wb.svg"
                                name={appMessages.home}
                                alt={appMessages.home}
                            />
                            <Subproduct
                                src="https://dam-cdn.atl.orangelogic.com/AssetLink/r7k15161ensv07cht48h87c31ueh7sh3.svg"
                                name={appMessages.goals}
                                alt={appMessages.goals}
                            />
                            <Subproduct
                                src="https://dam-cdn.atl.orangelogic.com/AssetLink/51ht267g50ve2d3051aw8nrgs0k11ad6.svg"
                                name={appMessages.teams}
                                alt={appMessages.teams}
                            />
                            <Subproduct
                                src="https://dam-cdn.atl.orangelogic.com/AssetLink/2pqaabre17cu7mx66ydvhplnbt4hh3ub.svg"
                                name={appMessages.studio}
                                alt={appMessages.studio}
                            />
                            <Subproduct
                                src="https://dam-cdn.atl.orangelogic.com/AssetLink/to7s6r4xr4564x6jhc134qhgevi13wo3.svg"
                                name={appMessages.search}
                                alt={appMessages.search}
                            />
                            <Subproduct
                                src="https://dam-cdn.atl.orangelogic.com/AssetLink/pe661ex05dvkmxul5nf4fi1e20hq8063.svg"
                                name={appMessages.chat}
                                alt={appMessages.chat}
                            />
                            <Subproduct
                                src="https://dam-cdn.atl.orangelogic.com/AssetLink/8f6ta76fw1d04u40k8fp622i67154764.svg"
                                name={appMessages.analytics}
                                alt={appMessages.analytics}
                            />
                            <Subproduct
                                src="https://dam-cdn.atl.orangelogic.com/AssetLink/dv817l8n01hct543cmd030ksu3hfj0k6.svg"
                                name={appMessages.admin}
                                alt={appMessages.admin}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

interface SubproductProps {
    name: string;
    src: string;
    alt: string;
}

const Subproduct = (props: SubproductProps) => {
    return (
        <div
            css={css({
                display: "flex",
                flexDirection: "column",
                gap: token("space.050"),
                minWidth: "54px",
                padding: `0 ${token("space.100")} ${token("space.100")} ${token(
                    "space.100"
                )}`
            })}
        >
            <div
                css={css({
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center"
                })}
            >
                <div
                    css={css({ margin: "9px", height: "20px", width: "20px" })}
                >
                    <Picture
                        image={{
                            src: props.src,
                            alt: props.alt
                        }}
                    />
                </div>
            </div>
            <span
                css={css({
                    textAlign: "center",
                    fontFamily: wacToken("font.charlie.text"),
                    fontSize: "10.5px",
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "12px"
                })}
            >
                {props.name}
            </span>
        </div>
    );
};

function useAppMessages() {
    const intl = useIntl();
    return {
        home: intl.formatMessage({
            id: "CollectionPricingHero.app.home.alt",
            defaultMessage: "Home"
        }),
        goals: intl.formatMessage({
            id: "CollectionPricingHero.app.goals",
            defaultMessage: "Goals"
        }),
        teams: intl.formatMessage({
            id: "CollectionPricingHero.app.teams",
            defaultMessage: "Teams"
        }),
        studio: intl.formatMessage({
            id: "CollectionPricingHero.app.studio",
            defaultMessage: "Studio"
        }),
        search: intl.formatMessage({
            id: "CollectionPricingHero.app.search",
            defaultMessage: "Search"
        }),
        chat: intl.formatMessage({
            id: "CollectionPricingHero.app.chat",
            defaultMessage: "Chat"
        }),
        analytics: intl.formatMessage({
            id: "CollectionPricingHero.app.analytics",
            defaultMessage: "Analytics"
        }),
        admin: intl.formatMessage({
            id: "CollectionPricingHero.app.admin",
            defaultMessage: "Admin"
        })
    } as const;
}
